/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect } from "react";
import { 
  BookOpen, 
  Upload, 
  Code, 
  Bot, 
  Github, 
  Gitlab,
  Key,
  Terminal,
  Check,
  Copy,
  Menu,
  X,
  Zap,
  Lock,
  Settings
} from "lucide-react";
import { cn } from "../utils/cn";

interface Section {
  id: string;
  title: string;
  icon: React.ElementType;
}

const sections: Section[] = [
  { id: "getting-started", title: "GETTING STARTED", icon: BookOpen },
  { id: "uploading-specs", title: "UPLOADING API SPECS", icon: Upload },
  { id: "viewing-docs", title: "VIEWING DOCUMENTATION", icon: Code },
  { id: "ai-assistant", title: "AI ASSISTANT", icon: Bot },
  { id: "cicd-integration", title: "CI/CD INTEGRATION", icon: Terminal },
  { id: "authentication", title: "AUTHENTICATION", icon: Lock },
  { id: "api-reference", title: "API REFERENCE", icon: Key },
];

export function Documentation() {
  const [activeSection, setActiveSection] = useState("getting-started");
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const copyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const CodeBlock = ({ code, language, id }: { code: string; language: string; id: string }) => (
    <div className="border-2 border-zinc-900 bg-zinc-50 my-4 overflow-hidden">
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-b-2 border-zinc-900 bg-white">
        <span className="text-xs font-medium text-zinc-500 tracking-wider">{language}</span>
        <button
          onClick={() => copyCode(code, id)}
          className="flex items-center gap-1.5 sm:gap-2 text-xs text-zinc-600 hover:text-zinc-900 transition-colors"
        >
          {copiedCode === id ? (
            <>
              <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">COPIED</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">COPY</span>
            </>
          )}
        </button>
      </div>
      <pre className="p-3 sm:p-4 overflow-x-auto text-xs sm:text-sm">
        <code className="font-mono text-zinc-900 whitespace-pre">{code}</code>
      </pre>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-2 border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src="/docgether.png" alt="DocGether" className="h-6 sm:h-8 w-auto" />
              <span className="text-zinc-300 hidden sm:inline">|</span>
              <span className="text-xs sm:text-sm font-medium text-zinc-900 tracking-wider">DOCS</span>
            </div>
            <div className="flex items-center gap-3">
              <a 
                href="/" 
                className="text-xs font-medium text-zinc-500 hover:text-zinc-900 tracking-wider transition-colors hidden sm:inline"
              >
                BACK TO APP →
              </a>
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-14 sm:pt-16 flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:block fixed left-0 top-14 sm:top-16 bottom-0 w-64 xl:w-72 border-r-2 border-zinc-200 overflow-y-auto bg-zinc-50">
          <div className="p-4 xl:p-6">
            <h2 className="text-xs font-bold text-zinc-400 tracking-wider mb-4">CONTENTS</h2>
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={cn(
                    "w-full flex items-center gap-2 xl:gap-3 px-3 xl:px-4 py-2.5 xl:py-3 text-left text-xs xl:text-sm font-medium transition-colors border-2",
                    activeSection === section.id
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : "bg-white text-zinc-600 border-transparent hover:border-zinc-200 hover:bg-zinc-100"
                  )}
                >
                  <section.icon className="w-3.5 h-3.5 xl:w-4 xl:h-4 shrink-0" />
                  <span className="truncate">{section.title}</span>
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && (
          <>
            <div 
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="lg:hidden fixed inset-y-0 left-0 w-72 bg-white z-50 border-r-2 border-zinc-900 overflow-y-auto">
              <div className="p-4 border-b-2 border-zinc-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-bold text-zinc-400 tracking-wider">CONTENTS</h2>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 border-2 border-zinc-200 hover:border-zinc-900 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <nav className="p-4 space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium transition-colors border-2",
                      activeSection === section.id
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-900"
                    )}
                  >
                    <section.icon className="w-4 h-4 shrink-0" />
                    {section.title}
                  </button>
                ))}
              </nav>
              <div className="p-4 border-t-2 border-zinc-200">
                <a 
                  href="/" 
                  className="flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>← Back to App</span>
                </a>
              </div>
            </div>
          </>
        )}

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 xl:ml-72 min-w-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            {/* Hero */}
            <div className="mb-12 sm:mb-16 border-2 border-zinc-900 p-6 sm:p-8 lg:p-12">
              <div className="flex items-center gap-2 sm:gap-3 mb-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-zinc-900 flex items-center justify-center shrink-0">
                  <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <span className="text-[10px] sm:text-xs font-bold text-zinc-500 tracking-wider">DOCTOGETHER GUIDE</span>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-zinc-900 mb-4">
                Documentation
              </h1>
              <p className="text-base sm:text-lg text-zinc-600 leading-relaxed">
                Learn how to use DocGether to generate beautiful, interactive API documentation 
                from your OpenAPI specifications.
              </p>
            </div>

            {/* Getting Started */}
            <section id="getting-started" className="mb-12 sm:mb-16 scroll-mt-20">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-zinc-100 border-2 border-zinc-900 flex items-center justify-center shrink-0">
                  <span className="text-xs sm:text-sm font-bold">01</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-zinc-900">Getting Started</h2>
              </div>
              
              <div className="prose prose-zinc max-w-none">
                <p className="text-zinc-600 leading-relaxed mb-6 text-sm sm:text-base">
                  DocGether transforms your OpenAPI specifications into beautiful, interactive documentation. 
                  Whether you have a Swagger 2.0 or OpenAPI 3.0 spec, our platform handles it all.
                </p>

                <h3 className="text-base sm:text-lg font-bold text-zinc-900 mb-4 mt-8">What You Will Need</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
                  {[
                    { icon: Code, title: "OpenAPI Spec", desc: "Swagger 2.0 or OpenAPI 3.0 JSON file" },
                    { icon: Upload, title: "Account", desc: "Free DocGether account to save docs" },
                    { icon: Zap, title: "Optional", desc: "Generate spec API key for CI/CD updates" },
                  ].map((item, i) => (
                    <div key={i} className="border-2 border-zinc-200 p-4 bg-white">
                      <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-zinc-900 mb-3" />
                      <h4 className="font-bold text-zinc-900 mb-1 text-sm sm:text-base">{item.title}</h4>
                      <p className="text-xs sm:text-sm text-zinc-500">{item.desc}</p>
                    </div>
                  ))}
                </div>

                <h3 className="text-base sm:text-lg font-bold text-zinc-900 mb-4">Quick Start Steps</h3>
                <div className="space-y-3">
                  {[
                    "Sign up for a free DocGether account",
                    "Upload your OpenAPI specification file",
                    "Get instant documentation with interactive endpoints",
                    "Share the URL with your team or customers",
                    "Set up CI/CD for automatic updates (optional)",
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 sm:w-8 sm:h-8 bg-zinc-900 text-white flex items-center justify-center text-xs sm:text-sm font-bold shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-zinc-700 pt-0.5 sm:pt-1 text-sm sm:text-base">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Uploading Specs */}
            <section id="uploading-specs" className="mb-12 sm:mb-16 scroll-mt-20">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-zinc-100 border-2 border-zinc-900 flex items-center justify-center shrink-0">
                  <span className="text-xs sm:text-sm font-bold">02</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-zinc-900">Uploading API Specs</h2>
              </div>

              <div className="space-y-4 sm:space-y-6">
                <p className="text-zinc-600 leading-relaxed text-sm sm:text-base">
                  DocGether accepts OpenAPI specifications in JSON format. You can upload your spec 
                  through the web interface or programmatically via our API.
                </p>

                <div className="border-2 border-zinc-900 bg-zinc-50 p-4 sm:p-6">
                  <h3 className="font-bold text-zinc-900 mb-4 flex items-center gap-2 text-sm sm:text-base">
                    <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                    Web Upload
                  </h3>
                  <ol className="space-y-2 text-zinc-700 text-sm sm:text-base">
                    <li>1. Log in to your DocGether dashboard</li>
                    <li>2. Click "Upload New Specification"</li>
                    <li>3. Select your OpenAPI JSON file or paste the URL</li>
                    <li>4. Click "Generate Documentation"</li>
                    <li>5. Your docs are live instantly - copy the shareable URL</li>
                  </ol>
                </div>

                <div className="border-2 border-zinc-200 bg-white p-4 sm:p-6">
                  <h3 className="font-bold text-zinc-900 mb-4 flex items-center gap-2 text-sm sm:text-base">
                    <Code className="w-4 h-4 sm:w-5 sm:h-5" />
                    Supported Formats
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      "OpenAPI 3.0",
                      "OpenAPI 3.1", 
                      "Swagger 2.0",
                      "JSON & YAML"
                    ].map((format, i) => (
                      <div key={i} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border border-zinc-200">
                        <Check className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 shrink-0" />
                        <span className="text-xs sm:text-sm">{format}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Viewing Docs */}
            <section id="viewing-docs" className="mb-12 sm:mb-16 scroll-mt-20">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-zinc-100 border-2 border-zinc-900 flex items-center justify-center shrink-0">
                  <span className="text-xs sm:text-sm font-bold">03</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-zinc-900">Viewing Documentation</h2>
              </div>

              <div className="space-y-4 sm:space-y-6">
                <p className="text-zinc-600 leading-relaxed text-sm sm:text-base">
                  Once uploaded, your API documentation includes an interactive interface with 
                  endpoint details, request/response schemas, and a "Try It Out" feature.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="border-2 border-zinc-200 p-4 sm:p-5">
                    <h3 className="font-bold text-zinc-900 mb-3 text-sm sm:text-base">Sidebar Navigation</h3>
                    <p className="text-xs sm:text-sm text-zinc-600 mb-4">
                      Browse all endpoints grouped by tags. Click any endpoint to view details.
                    </p>
                    <ul className="text-xs sm:text-sm text-zinc-500 space-y-2">
                      <li>• Filter by HTTP method (GET, POST, etc.)</li>
                      <li>• Search endpoints by name</li>
                      <li>• Expand/collapse tag groups</li>
                    </ul>
                  </div>

                  <div className="border-2 border-zinc-200 p-4 sm:p-5">
                    <h3 className="font-bold text-zinc-900 mb-3 text-sm sm:text-base">Endpoint Details</h3>
                    <p className="text-xs sm:text-sm text-zinc-600 mb-4">
                      Each endpoint shows comprehensive information:
                    </p>
                    <ul className="text-xs sm:text-sm text-zinc-500 space-y-2">
                      <li>• Path parameters & query parameters</li>
                      <li>• Request body schemas with examples</li>
                      <li>• Response codes and schemas</li>
                    </ul>
                  </div>
                </div>

                <div className="border-2 border-zinc-900 bg-zinc-50 p-4 sm:p-6">
                  <h3 className="font-bold text-zinc-900 mb-3 flex items-center gap-2 text-sm sm:text-base">
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
                    Try It Out Feature
                  </h3>
                  <p className="text-zinc-600 mb-4 text-sm sm:text-base">
                    Test API endpoints directly from the documentation:
                  </p>
                  <ol className="space-y-2 text-xs sm:text-sm text-zinc-700">
                    <li>1. Click "Try It Out" on any endpoint</li>
                    <li>2. Fill in required parameters</li>
                    <li>3. Add authentication if needed (Bearer token, API key, etc.)</li>
                    <li>4. Click "Send Request" to see the live response</li>
                  </ol>
                </div>
              </div>
            </section>

            {/* AI Assistant */}
            <section id="ai-assistant" className="mb-12 sm:mb-16 scroll-mt-20">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-zinc-100 border-2 border-zinc-900 flex items-center justify-center shrink-0">
                  <span className="text-xs sm:text-sm font-bold">04</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-zinc-900">AI Assistant</h2>
              </div>

              <div className="space-y-4 sm:space-y-6">
                <p className="text-zinc-600 leading-relaxed text-sm sm:text-base">
                  DocGether includes an AI-powered assistant that understands your API specification 
                  and can answer questions, generate code examples, and help developers integrate faster.
                </p>

                <div className="border-2 border-zinc-900 bg-zinc-900 text-white p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4">
                    <Bot className="w-5 h-5 sm:w-6 sm:h-6" />
                    <h3 className="font-bold text-sm sm:text-base">AI Assistant</h3>
                  </div>
                  <p className="text-zinc-300 text-sm sm:text-base">
                    The AI assistant has full context of your API specification and can help with:
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {[
                    "Find the right endpoint for specific tasks",
                    "Explain request/response schemas",
                    "Generate code examples in any language",
                    "Troubleshoot API integration issues",
                    "Answer questions about authentication",
                    "Suggest best practices for API usage",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 border border-zinc-200">
                      <Check className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
                      <span className="text-xs sm:text-sm text-zinc-700">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="border-2 border-zinc-200 p-4 sm:p-6">
                  <h3 className="font-bold text-zinc-900 mb-4 text-sm sm:text-base">How to Access</h3>
                  <ol className="space-y-2 text-zinc-700 text-sm sm:text-base">
                    <li>1. Open any API documentation page</li>
                    <li>2. Click the "AI Assistant" button in the top navigation</li>
                    <li>3. Type your question or request</li>
                    <li>4. Get instant, context-aware responses</li>
                  </ol>
                </div>
              </div>
            </section>

            {/* CI/CD Integration */}
            <section id="cicd-integration" className="mb-12 sm:mb-16 scroll-mt-20">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-zinc-100 border-2 border-zinc-900 flex items-center justify-center shrink-0">
                  <span className="text-xs sm:text-sm font-bold">05</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-zinc-900">CI/CD Integration</h2>
              </div>

              <div className="space-y-4 sm:space-y-6">
                <p className="text-zinc-600 leading-relaxed text-sm sm:text-base">
                  Automate documentation updates with our webhook API. Whenever you update your 
                  OpenAPI specification, your documentation stays in sync automatically.
                </p>

                <div className="border-2 border-zinc-900 p-4 sm:p-6">
                  <h3 className="font-bold text-zinc-900 mb-4 text-sm sm:text-base">Step 1: Generate an API Key for Your Spec</h3>
                  <p className="text-xs sm:text-sm text-zinc-600 mb-4">
                    API keys are tied to specific API specifications. Each spec you upload can have its own API key for CI/CD updates.
                  </p>
                  <div className="space-y-2 sm:space-y-3">
                    {[
                      "Log in to your DocGether account",
                      "Go to your Dashboard",
                      "Find the API specification you want to auto-update",
                      "Click the API KEY button on that spec card",
                      "Enter a name for your key (e.g., \"GitHub Actions\")",
                      "Click GENERATE KEY",
                      "Copy the generated API key (starts with dg_)",
                    ].map((step, i) => (
                      <div key={i} className="flex items-start gap-2 sm:gap-3">
                        <span className="w-5 h-5 sm:w-6 sm:h-6 bg-zinc-900 text-white flex items-center justify-center text-xs font-bold shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-zinc-700 text-xs sm:text-sm pt-0.5">{step}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200">
                    <p className="text-xs sm:text-sm text-amber-800">
                      <strong>Important:</strong> Copy the key immediately - you won't be able to see it again!
                    </p>
                  </div>
                </div>

                {/* GitHub Actions */}
                <div className="border-2 border-zinc-200 p-4 sm:p-6">
                  <h3 className="font-bold text-zinc-900 mb-4 flex items-center gap-2 text-sm sm:text-base">
                    <Github className="w-4 h-4 sm:w-5 sm:h-5" />
                    GitHub Actions Setup
                  </h3>
                  
                  <div className="space-y-4 sm:space-y-6">
                    <div>
                      <h4 className="font-bold text-zinc-900 mb-3 text-sm">Step 2: Add Secret to GitHub</h4>
                      <ol className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-zinc-700">
                        <li>1. Go to your GitHub repository</li>
                        <li>2. Click Settings → Secrets and variables → Actions</li>
                        <li>3. Click New repository secret</li>
                        <li>4. Name: <code className="bg-zinc-100 px-1.5 py-0.5 text-xs">DOCTOGETHER_API_KEY</code></li>
                        <li>5. Value: Paste your DocGether API key</li>
                        <li>6. Click Add secret</li>
                      </ol>
                    </div>

                    <div>
                      <h4 className="font-bold text-zinc-900 mb-3 text-sm">Step 3: Create Workflow File</h4>
                      <p className="text-xs sm:text-sm text-zinc-600 mb-4">
                        Create this file: <code className="bg-zinc-100 px-1.5 py-0.5 text-xs">.github/workflows/update-docs.yml</code>
                      </p>
                      <CodeBlock
                        id="github-action"
                        language="YAML"
                        code={`name: Update API Documentation

on:
  push:
    branches: [ main, master ]
    paths:
      - 'backend/openapi.json'

jobs:
  update-docs:
    runs-on: ubuntu-latest
    steps:
      - name: Checkout code
        uses: actions/checkout@v4
      
      - name: Update DocGether Documentation
        run: |
          curl -X POST \\
            "https://docgether.vibrantpx.xyz/api/webhook/update-spec?key=\${{ secrets.DOCTOGETHER_API_KEY }}" \\
            -H "Content-Type: application/json" \\
            -d @backend/openapi.json`}
                      />
                    </div>

                    <div>
                      <h4 className="font-bold text-zinc-900 mb-3 text-sm">Step 4: Test It</h4>
                      <ol className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-zinc-700">
                        <li>1. Commit and push the workflow file</li>
                        <li>2. Make a change to <code className="bg-zinc-100 px-1.5 py-0.5 text-xs">backend/openapi.json</code></li>
                        <li>3. Push the change to GitHub</li>
                        <li>4. Go to Actions tab in your repository</li>
                        <li>5. You should see the workflow running</li>
                        <li>6. Check DocGether - your docs should be updated!</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* GitLab CI */}
                <div className="border-2 border-zinc-200 p-4 sm:p-6">
                  <h3 className="font-bold text-zinc-900 mb-4 flex items-center gap-2 text-sm sm:text-base">
                    <Gitlab className="w-4 h-4 sm:w-5 sm:h-5" />
                    GitLab CI Setup
                  </h3>
                  
                  <div className="space-y-4 sm:space-y-6">
                    <div>
                      <h4 className="font-bold text-zinc-900 mb-3 text-sm">Step 2: Add Variable to GitLab</h4>
                      <ol className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-zinc-700">
                        <li>1. Go to your GitLab project</li>
                        <li>2. Click Settings → CI/CD → Variables</li>
                        <li>3. Click Add variable</li>
                        <li>4. Key: <code className="bg-zinc-100 px-1.5 py-0.5 text-xs">DOCTOGETHER_API_KEY</code></li>
                        <li>5. Value: Paste your DocGether API key</li>
                        <li>6. Check Mask variable (hides it in logs)</li>
                        <li>7. Click Add variable</li>
                      </ol>
                    </div>

                    <div>
                      <h4 className="font-bold text-zinc-900 mb-3 text-sm">Step 3: Create CI File</h4>
                      <p className="text-xs sm:text-sm text-zinc-600 mb-4">
                        Create this file: <code className="bg-zinc-100 px-1.5 py-0.5 text-xs">.gitlab-ci.yml</code>
                      </p>
                      <CodeBlock
                        id="gitlab-ci"
                        language="YAML"
                        code={`stages:
  - deploy

update-docs:
  stage: deploy
  image: curlimages/curl:latest
  rules:
    - if: $CI_COMMIT_BRANCH == "main" || $CI_COMMIT_BRANCH == "master"
      changes:
        - backend/openapi.json
  script:
    - echo "Updating DocGether documentation..."
    - |
      curl -X POST \\
        "https://docgether.vibrantpx.xyz/api/webhook/update-spec?key=$DOCTOGETHER_API_KEY" \\
        -H "Content-Type: application/json" \\
        -d @backend/openapi.json
    - echo "Documentation updated successfully!"`}
                      />
                    </div>

                    <div>
                      <h4 className="font-bold text-zinc-900 mb-3 text-sm">Step 4: Test It</h4>
                      <ol className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-zinc-700">
                        <li>1. Commit and push the <code className="bg-zinc-100 px-1.5 py-0.5 text-xs">.gitlab-ci.yml</code> file</li>
                        <li>2. Make a change to <code className="bg-zinc-100 px-1.5 py-0.5 text-xs">backend/openapi.json</code></li>
                        <li>3. Push the change to GitLab</li>
                        <li>4. Go to CI/CD → Pipelines in your project</li>
                        <li>5. You should see the pipeline running</li>
                        <li>6. Check DocGether - your docs should be updated!</li>
                      </ol>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 border-2 border-emerald-200 p-4 sm:p-6">
                  <h3 className="font-bold text-emerald-900 mb-2 text-sm sm:text-base">Security Best Practices</h3>
                  <ul className="text-emerald-800 text-xs sm:text-sm space-y-1.5 sm:space-y-2">
                    <li>• Never commit your API key to the repository</li>
                    <li>• Always use secrets/variables in your CI/CD platform</li>
                    <li>• Mask the variable so it doesn't appear in logs</li>
                    <li>• Rotate your API key periodically for security</li>
                  </ul>
                </div>

                <div className="bg-zinc-100 border-2 border-zinc-300 p-4 sm:p-6">
                  <h3 className="font-bold text-zinc-900 mb-3 text-sm sm:text-base">Troubleshooting</h3>
                  <div className="space-y-3 sm:space-y-4 text-xs sm:text-sm text-zinc-700">
                    <div>
                      <strong>Workflow not triggering?</strong>
                      <ul className="list-disc list-inside mt-1 ml-2 space-y-0.5">
                        <li>Make sure you're pushing to the correct branch (main/master)</li>
                        <li>Check that openapi.json is actually being modified</li>
                        <li>Verify the file path in the workflow matches your repo structure</li>
                      </ul>
                    </div>
                    <div>
                      <strong>Getting 401 errors?</strong>
                      <ul className="list-disc list-inside mt-1 ml-2 space-y-0.5">
                        <li>Verify your API key is correct</li>
                        <li>Check that the secret/variable name matches exactly</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Authentication */}
            <section id="authentication" className="mb-12 sm:mb-16 scroll-mt-20">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-zinc-100 border-2 border-zinc-900 flex items-center justify-center shrink-0">
                  <span className="text-xs sm:text-sm font-bold">06</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-zinc-900">Authentication</h2>
              </div>

              <div className="space-y-4 sm:space-y-6">
                <p className="text-zinc-600 leading-relaxed text-sm sm:text-base">
                  DocGether supports multiple authentication methods for accessing the platform 
                  and for testing APIs through the documentation.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="border-2 border-zinc-200 p-4 sm:p-5">
                    <h3 className="font-bold text-zinc-900 mb-3 text-sm sm:text-base">Platform Access</h3>
                    <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span>Username & Password</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Github className="w-4 h-4 shrink-0" />
                        <span>GitHub OAuth</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Gitlab className="w-4 h-4 shrink-0" />
                        <span>GitLab OAuth</span>
                      </li>
                    </ul>
                  </div>

                  <div className="border-2 border-zinc-200 p-4 sm:p-5">
                    <h3 className="font-bold text-zinc-900 mb-3 text-sm sm:text-base">API Testing Auth</h3>
                    <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                      <li className="flex items-center gap-2">
                        <Key className="w-4 h-4 shrink-0" />
                        <span>API Key (header/query)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Lock className="w-4 h-4 shrink-0" />
                        <span>Bearer Token (JWT)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Settings className="w-4 h-4 shrink-0" />
                        <span>OAuth2 / Basic Auth</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="border-2 border-zinc-900 p-4 sm:p-6">
                  <h3 className="font-bold text-zinc-900 mb-4 text-sm sm:text-base">Setting Up API Authentication</h3>
                  <p className="text-zinc-600 mb-4 text-sm sm:text-base">
                    When testing endpoints that require authentication:
                  </p>
                  <ol className="space-y-2 text-xs sm:text-sm text-zinc-700">
                    <li>1. Click the lock icon in the navbar</li>
                    <li>2. Select your authentication type</li>
                    <li>3. Enter your credentials</li>
                    <li>4. Credentials are stored for your session only</li>
                    <li>5. All "Try It Out" requests will include the auth headers</li>
                  </ol>
                </div>
              </div>
            </section>

            {/* API Reference */}
            <section id="api-reference" className="mb-12 sm:mb-16 scroll-mt-20">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-zinc-100 border-2 border-zinc-900 flex items-center justify-center shrink-0">
                  <span className="text-xs sm:text-sm font-bold">07</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-zinc-900">API Reference</h2>
              </div>

              <div className="space-y-4 sm:space-y-6">
                <p className="text-zinc-600 leading-relaxed text-sm sm:text-base">
                  DocGether provides a simple webhook API for updating documentation programmatically.
                </p>

                <div className="border-2 border-zinc-900 p-4 sm:p-6 overflow-x-auto">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4">
                    <span className="bg-emerald-100 text-emerald-800 px-2 sm:px-3 py-1 text-xs font-bold">POST</span>
                    <code className="text-xs sm:text-sm bg-zinc-100 px-2 py-1 whitespace-nowrap">/api/webhook/update-spec</code>
                  </div>
                  <p className="text-zinc-600 mb-4 text-sm sm:text-base">
                    Update or create API documentation from an OpenAPI specification.
                  </p>

                  <h4 className="font-bold text-zinc-900 mb-2 text-sm sm:text-base">Query Parameters</h4>
                  <div className="overflow-x-auto mb-6">
                    <table className="w-full text-xs sm:text-sm min-w-[400px]">
                      <thead className="border-b-2 border-zinc-200">
                        <tr>
                          <th className="text-left py-2 font-bold">Parameter</th>
                          <th className="text-left py-2 font-bold">Type</th>
                          <th className="text-left py-2 font-bold">Description</th>
                        </tr>
                      </thead>
                      <tbody className="text-zinc-600">
                        <tr className="border-b border-zinc-100">
                          <td className="py-2 font-mono">key</td>
                          <td className="py-2">string</td>
                          <td className="py-2">Your DocGether API key</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h4 className="font-bold text-zinc-900 mb-2 text-sm sm:text-base">Request Body</h4>
                  <p className="text-xs sm:text-sm text-zinc-600 mb-4">
                    The complete OpenAPI specification as JSON.
                  </p>

                  <h4 className="font-bold text-zinc-900 mb-2 text-sm sm:text-base">Example Request</h4>
                  <CodeBlock
                    id="api-example"
                    language="BASH"
                    code={`curl -X POST \\
  "https://docgether.vibrantpx.xyz/api/webhook/update-spec?key=YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d @openapi.json`}
                  />

                  <h4 className="font-bold text-zinc-900 mb-2 mt-4 sm:mt-6 text-sm sm:text-base">Response</h4>
                  <CodeBlock
                    id="api-response"
                    language="JSON"
                    code={`{
  "success": true,
  "id": "abc123",
  "url": "https://docgether.vibrantpx.xyz/?id=abc123",
  "message": "Documentation updated successfully"
}`}
                  />
                </div>
              </div>
            </section>

            {/* Footer */}
            <div className="border-t-2 border-zinc-200 pt-8 sm:pt-12 mt-12 sm:mt-16">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <img src="/docgether.png" alt="DocGether" className="h-5 sm:h-6 w-auto" />
                  <span className="text-xs sm:text-sm text-zinc-500">
                    © {new Date().getFullYear()} DocGether. All rights reserved.
                  </span>
                </div>
                <div className="flex items-center gap-4 sm:gap-6">
                  <a href="/privacy" className="text-xs sm:text-sm text-zinc-500 hover:text-zinc-900 transition-colors">
                    Privacy
                  </a>
                  <a href="/terms" className="text-xs sm:text-sm text-zinc-500 hover:text-zinc-900 transition-colors">
                    Terms
                  </a>
                  <a href="/contact" className="text-xs sm:text-sm text-zinc-500 hover:text-zinc-900 transition-colors">
                    Contact
                  </a>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
